/**
 * Native browser TTS using window.speechSynthesis.
 * Zero cost, runs entirely on-device.
 */

let voicesLoaded = false;
let currentUtterance: SpeechSynthesisUtterance | null = null;

/**
 * Get available system TTS voices.
 * Voices load asynchronously on some browsers, so we wait for them.
 */
export function getAvailableVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    const voices = speechSynthesis.getVoices();
    if (voices.length > 0) {
      voicesLoaded = true;
      resolve(voices);
      return;
    }

    // Chrome loads voices asynchronously
    speechSynthesis.onvoiceschanged = () => {
      voicesLoaded = true;
      resolve(speechSynthesis.getVoices());
    };

    // Fallback timeout in case event never fires
    setTimeout(() => {
      resolve(speechSynthesis.getVoices());
    }, 1000);
  });
}

/**
 * Speak text using native TTS.
 */
export async function speak(
  text: string,
  options?: {
    voiceName?: string;
    rate?: number;
    pitch?: number;
    onStart?: () => void;
    onEnd?: () => void;
    onError?: (error: string) => void;
  },
): Promise<void> {
  if (!('speechSynthesis' in window)) {
    console.warn('[TTS] speechSynthesis not available');
    options?.onError?.('Speech synthesis not available in this browser');
    return;
  }

  // Cancel any current speech
  stop();

  const utterance = new SpeechSynthesisUtterance(text);
  currentUtterance = utterance;

  // Set voice if specified
  if (options?.voiceName) {
    const voices = await getAvailableVoices();
    const voice = voices.find(v => v.name === options.voiceName);
    if (voice) {
      utterance.voice = voice;
    }
  }

  utterance.rate = options?.rate ?? 1.0;
  utterance.pitch = options?.pitch ?? 1.0;

  utterance.onstart = () => {
    console.log('[TTS] Speaking...');
    options?.onStart?.();
  };

  utterance.onend = () => {
    console.log('[TTS] Done speaking');
    currentUtterance = null;
    options?.onEnd?.();
  };

  utterance.onerror = (event) => {
    // 'canceled' is expected when stop() is called
    if (event.error !== 'canceled') {
      console.error('[TTS] Error:', event.error);
      options?.onError?.(event.error);
    }
    currentUtterance = null;
  };

  speechSynthesis.speak(utterance);
}

/**
 * Stop any current speech.
 */
export function stop(): void {
  if (speechSynthesis.speaking || speechSynthesis.pending) {
    speechSynthesis.cancel();
    currentUtterance = null;
  }
}

/**
 * Check if TTS is currently speaking.
 */
export function isSpeaking(): boolean {
  return speechSynthesis.speaking;
}

/**
 * Check if native TTS is supported.
 */
export function isTTSSupported(): boolean {
  return 'speechSynthesis' in window;
}
